/**
 * TierPrivilegesBlocks — sub-components for TierPrivilegesEditor.
 * Extracted from TierPrivilegesEditor.jsx to reduce file complexity.
 */
import React from 'react';
import {
  ChevronRight, ChevronDown,
  Layers, Grid3X3, Monitor,
  CheckSquare, Square, MinusSquare
} from 'lucide-react';

// ── TriCheckbox ───────────────────────────────────────────────────────────────
export function TriCheckbox({ state, onClick, color = '#3b82f6', size = 16, testId }) {
  const Icon = state === 'all' ? CheckSquare : state === 'partial' ? MinusSquare : Square;
  const iconColor = state === 'none' ? '#cbd5e1' : color;
  return (
    <button
      onClick={onClick}
      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
      data-testid={testId}
    >
      <Icon size={size} color={iconColor} />
    </button>
  );
}

// ── Pill ──────────────────────────────────────────────────────────────────────
export function Pill({ icon, label, value, color }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: '4px 10px', borderRadius: 999,
      background: `${color || '#3b82f6'}15`, color: color || '#3b82f6',
      fontSize: 10, fontWeight: 700
    }}>
      {icon} <strong>{label}:</strong> {value}
    </span>
  );
}

// ── ModuleNode ────────────────────────────────────────────────────────────────
export function ModuleNode({
  mod, state, expanded, onToggleExpand, onToggle,
  subModuleState, expandedSubs, onToggleSubExpand, onToggleSub,
  screensEnabled, onToggleScreen, color,
  inIndustry, industryFiltered
}) {
  const dim = industryFiltered && !inIndustry('modules', mod.ModuleId);
  return (
    <div style={{ borderBottom: '1px solid #f1f5f9', opacity: dim ? 0.45 : 1 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 14px', background: state === 'all' ? `${color}10` : '#fff' }}>
        <button onClick={onToggleExpand} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2 }} data-testid={`mod-expand-${mod.ModuleId}`}>
          {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
        </button>
        <TriCheckbox state={state} onClick={onToggle} color={color} testId={`mod-check-${mod.ModuleId}`} />
        <Layers size={14} color={color} />
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, fontSize: 12, color: '#0f172a' }}>{mod.ModuleName}</div>
          <div style={{ fontSize: 9, color: '#94a3b8' }}>
            ID {mod.ModuleId} · {(mod.subModules || []).length} submodules · {(mod.subModules || []).flatMap(s => s.screens || []).length} screens
            {dim && <span style={{ marginLeft: 8, color: '#b45309', fontWeight: 700 }}>· not in industry</span>}
          </div>
        </div>
      </div>

      {expanded && (mod.subModules || []).map(sub => (
        <SubModuleNode
          key={sub.SubModuleId}
          sub={sub}
          state={subModuleState(sub)}
          expanded={expandedSubs.has(String(sub.SubModuleId))}
          onToggleExpand={() => onToggleSubExpand(sub.SubModuleId)}
          onToggle={() => onToggleSub(sub)}
          screensEnabled={screensEnabled}
          onToggleScreen={onToggleScreen}
          color={color}
          inIndustry={inIndustry}
          industryFiltered={industryFiltered}
        />
      ))}
    </div>
  );
}

// ── SubModuleNode ─────────────────────────────────────────────────────────────
export function SubModuleNode({ sub, state, expanded, onToggleExpand, onToggle, screensEnabled, onToggleScreen, color, inIndustry, industryFiltered }) {
  const screens = sub.screens || [];
  const dim = industryFiltered && inIndustry && !inIndustry('subModules', sub.SubModuleId);
  return (
    <div style={{ opacity: dim ? 0.5 : 1 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 14px 6px 38px', background: state === 'all' ? `${color}08` : 'transparent' }}>
        <button onClick={onToggleExpand} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2 }} disabled={screens.length === 0} data-testid={`sub-expand-${sub.SubModuleId}`}>
          {screens.length === 0 ? <span style={{ width: 14, display: 'inline-block' }} /> : expanded ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
        </button>
        <TriCheckbox state={state} onClick={onToggle} color={color} size={14} testId={`sub-check-${sub.SubModuleId}`} />
        <Grid3X3 size={12} color="#64748b" />
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 600, fontSize: 11, color: '#1e293b' }}>{sub.SubModuleName}</div>
          <div style={{ fontSize: 9, color: '#94a3b8' }}>{sub.SubModuleId} · {screens.length} screens</div>
        </div>
      </div>

      {expanded && screens.map(scr => {
        const checked = screensEnabled.has(String(scr.SeqId));
        const scrDim = industryFiltered && inIndustry && !inIndustry('screens', scr.SeqId);
        return (
          <div key={scr.SeqId} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 14px 4px 70px', background: checked ? `${color}05` : '#fff', opacity: scrDim ? 0.45 : 1 }}>
            <TriCheckbox state={checked ? 'all' : 'none'} onClick={() => onToggleScreen(scr.SeqId)} color={color} size={12} testId={`scr-check-${scr.SeqId}`} />
            <Monitor size={11} color="#94a3b8" />
            <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 11, color: '#0f172a' }}>{scr.ScreenTitle}</span>
              <span style={{ fontSize: 9, color: '#94a3b8', fontFamily: 'monospace' }}>{scr.ScreenMenuName}</span>
              <span style={{ fontSize: 9, color: '#94a3b8', marginLeft: 'auto', background: '#f1f5f9', padding: '1px 6px', borderRadius: 3 }}>{scr.ScreenType}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
}
